/**
 * MfaChallenge — Login-2nd-Faktor: TOTP-Code oder Recovery-Code verifizieren.
 *
 * Flow: listFactors → challenge → verify (TOTP) ODER consumeRecoveryCode.
 * Siehe: docs/frontend/FRONTEND-DESIGN-RULES.md
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupabaseClient } from '@/lib/supabase-client';
import { consumeRecoveryCode } from '@/lib/api/mfa';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';
import { EID } from '@/lib/element-ids.registry';

interface Props {
  onSuccess: () => void;
}

export function MfaChallenge({ onSuccess }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [error, setError] = useState('');

  const verifyTotp = async () => {
    const sb = await getSupabaseClient();
    const f = await sb.auth.mfa.listFactors();
    const factorId = f.data?.totp?.[0]?.id;
    if (!factorId) {
      setError(t('mfa.challenge.invalid'));
      return;
    }
    const ch = await sb.auth.mfa.challenge({ factorId });
    if (ch.error) {
      setError(ch.error.message);
      return;
    }
    const v = await sb.auth.mfa.verify({ factorId, challengeId: ch.data.id, code });
    if (v.error) {
      setError(t('mfa.challenge.invalid'));
      return;
    }
    onSuccess();
  };

  const useRecovery = async () => {
    try {
      await consumeRecoveryCode(code);
      onSuccess();
    } catch {
      setError(t('mfa.challenge.invalid'));
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-100">
        {t('mfa.challenge.title')}
      </h2>
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <Input
        data-testid="mfa-challenge-code"
        id={EID['mfa-challenge-code']}
        label={t('mfa.challenge.enterCode')}
        value={code}
        onChange={(e) => setCode(e.target.value.trim())}
        placeholder={t('mfa.challenge.enterCode')}
      />
      <Button
        data-testid="mfa-challenge-verify"
        id={EID['mfa-challenge-verify']}
        onClick={recoveryMode ? useRecovery : verifyTotp}
      >
        {t('mfa.challenge.verify')}
      </Button>
      <button
        type="button"
        id={EID['mfa-use-recovery']}
        onClick={() => setRecoveryMode(true)}
        className="block text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
      >
        {t('mfa.challenge.useRecovery')}
      </button>
    </div>
  );
}
